import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetLocaleSettingsResponse,
  LocaleSettings,
  LocaleSettingsCreatedEnvelope,
  LocaleSettingsDeletedEnvelope,
  LocaleSettingsMultilingualModeUpdatedEnvelope,
  LocaleSettingsUpdatedEnvelope,
  SetMultilingualModeApplicationErrors,
  SetMultilingualModeResponse,
  UpdateLocaleSettingsResponse,
  getLocaleSettings as universalGetLocaleSettings,
  setMultilingualMode as universalSetMultilingualMode,
  updateLocaleSettings as universalUpdateLocaleSettings,
} from './multilingual-settings-v2-locale-settings-locale-settings.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/multilingual' };

export function getLocaleSettings(
  httpClient: HttpClient
): GetLocaleSettingsSignature {
  return () =>
    universalGetLocaleSettings(
      // @ts-ignore
      { httpClient }
    );
}

interface GetLocaleSettingsSignature {
  /**
   * Retrieves a site's locale settings.
   * @returns Response message containing the requested multilingual settings
   */
  (): Promise<
    NonNullablePaths<
      GetLocaleSettingsResponse,
      | `localeSettings.urlStructure`
      | `localeSettings.primaryLocale.visibility`
      | `localeSettings.primaryLocale.effectiveUrlStructure`
      | `localeSettings.primaryLocale.urlStructureOverride.urlStructure`,
      5
    >
  >;
}

export function updateLocaleSettings(
  httpClient: HttpClient
): UpdateLocaleSettingsSignature {
  return (localeSettings: NonNullablePaths<LocaleSettings, `revision`, 2>) =>
    universalUpdateLocaleSettings(
      localeSettings,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateLocaleSettingsSignature {
  /**
   * Updates a site's locale settings.
   *
   * > **Note:**
   * > You can't call this method to update a site's multilingual mode. To enable or disable multilingual mode, call Set Multilingual Mode.
   * @param - Locale settings to update.
   * @returns Response message containing the updated multilingual settings
   */
  (localeSettings: NonNullablePaths<LocaleSettings, `revision`, 2>): Promise<
    NonNullablePaths<
      UpdateLocaleSettingsResponse,
      | `localeSettings.urlStructure`
      | `localeSettings.primaryLocale.visibility`
      | `localeSettings.primaryLocale.effectiveUrlStructure`
      | `localeSettings.primaryLocale.urlStructureOverride.urlStructure`,
      5
    >
  >;
}

export function setMultilingualMode(
  httpClient: HttpClient
): SetMultilingualModeSignature {
  return (multilingualModeEnabled: boolean) =>
    universalSetMultilingualMode(
      multilingualModeEnabled,
      // @ts-ignore
      { httpClient }
    );
}

interface SetMultilingualModeSignature {
  /**
   * Sets a site's multilingual mode.
   *
   * Multilingual mode allows a site to support multiple locales. This means that when multilingual mode is enabled, you can create and manage secondary locales.
   *
   * To disable multilingual mode, all secondary locales must first be removed. This action removes all translated content from your site. Disabling multilingual mode will also reset the locale settings to their default values.
   *
   * <blockquote class="important">
   * <strong>Important:</strong>
   *
   * You can only call this method on sites where a Wix user has installed the [Wix Multilingual app](https://www.wix.com/app-market/wix-multilingual?searchLocation=home).
   *
   * </blockquote>
   * @param - Whether to enable multilingual mode for the site.
   *
   * Default: `false`
   * @returns Response message containing the updated settings after changing the multilingual mode
   */
  (multilingualModeEnabled: boolean): Promise<
    NonNullablePaths<
      SetMultilingualModeResponse,
      | `localeSettings.urlStructure`
      | `localeSettings.primaryLocale.visibility`
      | `localeSettings.primaryLocale.effectiveUrlStructure`
      | `localeSettings.primaryLocale.urlStructureOverride.urlStructure`,
      5
    > & {
      __applicationErrorsType?: SetMultilingualModeApplicationErrors;
    }
  >;
}

export const onLocaleSettingsCreated = EventDefinition(
  'wix.multilingual.settings.v2.locale_settings_created',
  true,
  (event: LocaleSettingsCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.primaryLocale.createdDate' },
            { path: 'entity.primaryLocale.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LocaleSettingsCreatedEnvelope>();
export const onLocaleSettingsDeleted = EventDefinition(
  'wix.multilingual.settings.v2.locale_settings_deleted',
  true,
  (event: LocaleSettingsDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.primaryLocale.createdDate' },
            { path: 'undefined.primaryLocale.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LocaleSettingsDeletedEnvelope>();
export const onLocaleSettingsMultilingualModeUpdated = EventDefinition(
  'wix.multilingual.settings.v2.locale_settings_multilingual_mode_updated',
  true,
  (event: LocaleSettingsMultilingualModeUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.primaryLocale.createdDate' },
            { path: 'data.primaryLocale.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LocaleSettingsMultilingualModeUpdatedEnvelope>();
export const onLocaleSettingsUpdated = EventDefinition(
  'wix.multilingual.settings.v2.locale_settings_updated',
  true,
  (event: LocaleSettingsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.primaryLocale.createdDate' },
            { path: 'entity.primaryLocale.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.primaryLocale.createdDate' },
            { path: 'modifiedFields.primaryLocale.updatedDate' },
          ],
        },
      ])
    )
)<LocaleSettingsUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Asset,
  BaseEventMetadata,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteContext,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetLocaleSettingsRequest,
  GetLocaleSettingsResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Locale,
  LocaleSettings,
  LocaleSettingsCreatedEnvelope,
  LocaleSettingsDeletedEnvelope,
  LocaleSettingsMultilingualModeUpdatedEnvelope,
  LocaleSettingsUpdatedEnvelope,
  LocaleVisibility,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  MultilingualModeUpdated,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  QueryLocaleSettingsRequest,
  QueryLocaleSettingsResponse,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SetMultilingualModeRequest,
  SetMultilingualModeResponse,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  SortOrder,
  Sorting,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  UpdateLocaleSettingsRequest,
  UpdateLocaleSettingsResponse,
  UrlStructure,
  UrlStructureOverride,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './multilingual-settings-v2-locale-settings-locale-settings.universal.js';
